import {
  useQuery, useMutation, useQueryClient,
} from '@tanstack/react-query';
import {
  getUsers,
  getUser,
  inviteUser,
  updateUser,
  performUserAction,
  deleteUser,
  type UserFilters,
} from '../api/users-client';
import type {
  InviteUserRequest,
  UpdateUserRequest,
  UserAction,
} from '../types/users';

// Query keys
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: UserFilters) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
};

// List users
export function useUsers(filters?: UserFilters) {
  return useQuery({
    queryKey: userKeys.list(filters ?? {}),
    queryFn: () => getUsers(filters),
  });
}

// Get single user
export function useUser(userId: string) {
  return useQuery({
    queryKey: userKeys.detail(userId),
    queryFn: () => getUser(userId),
    enabled: Boolean(userId),
  });
}

// Invite user mutation
export function useInviteUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InviteUserRequest) => inviteUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

// Update user mutation
export function useUpdateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      userId, data 
    }: {
      userId: string;
      data: UpdateUserRequest;
    }) => updateUser(userId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.userId) });
    },
  });
}

// User action mutation (disable, enable, reset password, resend invite)
export function useUserAction() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      userId, action 
    }: {
      userId: string;
      action: UserAction;
    }) => performUserAction(userId, action),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.userId) });
    },
  });
}

// Delete user mutation
export function useDeleteUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}
